import * as React from 'react';
import { FunctionComponent } from 'react';
import propz from 'propz';
import Moment from 'moment';
import {
  TOURNAMENT_SUBTYPE_SERVER_TO_CLIENT_MAPPING,
  TOURNAMENT_TYPE_SERVER_TO_CLIENT_MAPPING
} from '../../consts/tournament';
import { formatAgesCompactForSchool } from 'Src/helpers/ageGroups';
import { Tournament } from '../../models/tournament';
import { Championship } from '../../models/championship';

interface Props {
  tournament: Tournament;
  championships: Championship[];
}

type SummaryItem = {
  label: string;
  value: React.ReactNode;
};

const SummaryInlineGroup: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <span className="tournamentSummaryInlineGroup">
    <span className="tournamentSummaryInlineGroupLabel">{label}</span>
    <span className="tournamentSummaryInlineGroupValue">{value}</span>
  </span>
);

export const TournamentSummary: FunctionComponent<Props> = ({ tournament, championships }) => {
  const championshipName = propz.get(
    championships.find(championship => championship.tournamentIds.some(tournamentId => tournamentId === tournament.id)),
    ['name'],
    'No'
  );

  const description = String(propz.get(tournament, ['description'], '') || '').trim();
  const typeLabel = TOURNAMENT_TYPE_SERVER_TO_CLIENT_MAPPING[tournament.type] || '';
  const subTypeLabel = TOURNAMENT_SUBTYPE_SERVER_TO_CLIENT_MAPPING[tournament.subType] || '';

  const sportLabel = String(propz.get(tournament, ['sport', 'name'], '') || '').trim();
  const typeSubTypeLabel =
    typeLabel && subTypeLabel ? `${typeLabel} / ${subTypeLabel}` : typeLabel || subTypeLabel || '';

  const genderLabelMap = {
    MALE_ONLY: 'Boys',
    FEMALE_ONLY: 'Girls',
    MIXED: 'Mixed'
  };

  const genderLabel = String(genderLabelMap[tournament.gender] || tournament.gender || '').trim();

  const agesLabel = formatAgesCompactForSchool(
    propz.get(tournament, ['ages'], []),
    propz.get(tournament, ['school'], null)
  );

  const genderAgesLabel = genderLabel && agesLabel ? `${genderLabel} • ${agesLabel}` : genderLabel || agesLabel || '';

  const venueName = String(
    propz.get(tournament, ['venue', 'placeData', 'name'], '') ||
      propz.get(tournament, ['venue', 'postcode'], '') ||
      propz.get(tournament, ['venue', 'postcodeData', 'postcode'], '') ||
      ''
  ).trim();

  const formatDateOnly = (value?: any): string => {
    return value ? Moment(value).format('DD/MM/YYYY') : '';
  };

  const formatTimeOnly = (value?: any): string => {
    return value ? Moment(value).format('HH:mm') : '';
  };

  const formatDateTimeRange = (from?: any, to?: any): string => {
    if (!from && !to) return '—';
    if (from && !to) return Moment(from).format('DD/MM/YYYY HH:mm');
    if (!from && to) return Moment(to).format('DD/MM/YYYY HH:mm');

    const sameDay = Moment(from).format('YYYY-MM-DD') === Moment(to).format('YYYY-MM-DD');

    if (sameDay) {
      return `${formatDateOnly(from)} ${formatTimeOnly(from)} - ${formatTimeOnly(to)}`;
    }

    return `${Moment(from).format('DD/MM/YYYY HH:mm')} - ${Moment(to).format('DD/MM/YYYY HH:mm')}`;
  };

  const eventWindow = formatDateTimeRange(tournament.startTime, tournament.endTime);
  const bookingWindow =
    tournament.entriesOpen || tournament.deadlineForEntries
      ? formatDateTimeRange(tournament.entriesOpen, tournament.deadlineForEntries)
      : '—';

  const items: SummaryItem[] = [
    {
      label: 'Event / Booking / Venue',
      value: (
        <div className="tournamentSummaryInlineGroups">
          <SummaryInlineGroup label="Event" value={eventWindow} />
          <SummaryInlineGroup label="Booking" value={bookingWindow} />
          <SummaryInlineGroup label="Venue" value={venueName || '—'} />
        </div>
      )
    },
    {
      label: 'Organisers',
      value: (
        <div className="tournamentSummaryInlineGroups">
          <SummaryInlineGroup label="Names" value={propz.get(tournament, ['organisers'], '—') || '—'} />
          <SummaryInlineGroup label="Email" value={propz.get(tournament, ['organisersEmail'], '—') || '—'} />
        </div>
      )
    },
    {
      label: 'Championship / Max players / Domain',
      value: (
        <div className="tournamentSummaryInlineGroups">
          <SummaryInlineGroup label="Championship" value={championshipName || 'No'} />
          <SummaryInlineGroup
            label="Max players"
            value={
              typeof propz.get(tournament, ['maxPlayersPerTeam']) !== 'undefined' &&
              propz.get(tournament, ['maxPlayersPerTeam']) !== null &&
              propz.get(tournament, ['maxPlayersPerTeam']) !== ''
                ? propz.get(tournament, ['maxPlayersPerTeam'])
                : '—'
            }
          />
          <SummaryInlineGroup label="Domain" value={propz.get(tournament, ['domain'], '—') || '—'} />
        </div>
      )
    }
  ];

  return (
    <div className="tournamentSummaryBody">
      <div className="tournamentSummaryHero">
        <div className="tournamentSummaryHeroMain">
          <div className="tournamentSummaryTitle">{propz.get(tournament, ['name'], 'Tournament')}</div>

          {description ? (
            <div className="tournamentSummaryDescription" dangerouslySetInnerHTML={{ __html: description }} />
          ) : (
            <div className="tournamentSummaryDescription tournamentSummaryDescription--muted">
              No description added yet.
            </div>
          )}
        </div>

        <div className="tournamentSummaryHeroSide">
          {sportLabel ? <div className="tournamentSummaryPill tournamentSummaryPill--sport">{sportLabel}</div> : null}
          {typeSubTypeLabel ? <div className="tournamentSummaryPill">{typeSubTypeLabel}</div> : null}
          {genderAgesLabel ? (
            <div className="tournamentSummaryPill tournamentSummaryPill--subtle">{genderAgesLabel}</div>
          ) : null}
        </div>
      </div>

      <div className="tournamentSummaryGrid">
        {items.map(item => (
          <div key={item.label} className="tournamentSummaryItem">
            <div className="tournamentSummaryItemLabel">{item.label}</div>
            <div className="tournamentSummaryItemValue">{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

TournamentSummary.displayName = 'TournamentSummary';

